import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMpMap } from '../../hooks/useMpMap.js';
import { useClientStore } from '../../store/client.store.js';
import { useAuthStore } from '../../store/auth.store.js';
import StoreCards from './StoreCards.jsx';
import StoreCanvas from './StoreCanvas.jsx';
import Spinner from '../Spinner.jsx';
import Button from '../Button.jsx';

function OrphanTerminals({ orphans, navigate }) {
  return (
    <div className="card border-amber-700/40 bg-amber-900/10 space-y-3">
      <p className="text-sm text-amber-300 font-medium">⚠ {orphans.length} terminal(es) en MP sin registrar en el sistema</p>
      <div className="space-y-2">
        {orphans.map((t) => (
          <button
            key={t.mp_terminal_id}
            onClick={() => navigate('/terminals', {
              state: {
                prefillTerminal: {
                  mpTerminalId: t.mp_terminal_id,
                  mpPosId:      t.mp_pos_id   || null,
                  mpStoreId:    t.mp_store_id || null,
                },
                autoOpen: true,
              },
            })}
            className="w-full text-left p-3 rounded-lg border border-amber-600/40 bg-amber-900/20
                       hover:border-amber-400/60 hover:bg-amber-900/30 transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-mono text-amber-200">{t.mp_terminal_id}</p>
                <div className="flex gap-2 flex-wrap">
                  {t.operating_mode && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-600">
                      {t.operating_mode}
                    </span>
                  )}
                  {t.status && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-600">
                      {t.status}
                    </span>
                  )}
                </div>
                {(t.mp_pos_id || t.mp_store_id) && (
                  <div className="flex gap-2 flex-wrap text-xs text-slate-400">
                    {t.mp_store_id && <span>Store MP: <span className="font-mono text-slate-300">{t.mp_store_id}</span></span>}
                    {t.mp_pos_id   && <span>POS MP: <span className="font-mono text-emerald-400">{t.mp_pos_id}</span></span>}
                  </div>
                )}
              </div>
              <span className="text-xs text-amber-400 shrink-0 ml-3">
                {t.mp_pos_id ? '→ Registrar y asignar' : '→ Registrar'}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MpMap({ autoLoad = false }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const [enabled, setEnabled] = useState(autoLoad);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showAllWarning, setShowAllWarning] = useState(false);

  const clientId = user?.role === 'superadmin' ? selectedClientId : user?.clientId;
  const { data, isLoading, isError, error } = useMpMap({ enabled });

  if (!clientId) {
    return (
      <div className="card text-center py-8 text-slate-400 text-sm">
        Seleccioná un cliente en el header para ver su mapa de infraestructura.
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="card flex flex-col items-center py-10 text-center space-y-4">
        <div className="text-4xl opacity-40">🗺</div>
        <p className="text-slate-300 font-medium">Mapa de infraestructura MP</p>
        <p className="text-slate-500 text-sm max-w-sm">
          Muestra stores, POS y terminales cruzando nuestra BD con la API de MercadoPago en tiempo real.
        </p>
        <Button onClick={() => setEnabled(true)}>Cargar mapa</Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card flex items-center justify-center py-12 gap-3">
        <Spinner size="lg" />
        <span className="text-slate-400 text-sm">Consultando MercadoPago...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card text-center py-8 space-y-2">
        <p className="text-red-400 text-sm">Error: {error?.message}</p>
        <Button variant="secondary" onClick={() => setEnabled(false)}>Reintentar</Button>
      </div>
    );
  }

  const stores = data?.stores || [];
  const orphans = data?.orphan_terminals || [];

  // Vista canvas de una store específica
  if (selectedStore) {
    return (
      <div className="space-y-4">
        <StoreCanvas store={selectedStore} onBack={() => setSelectedStore(null)} />
        {orphans.length > 0 && <OrphanTerminals orphans={orphans} navigate={navigate} />}
      </div>
    );
  }

  // Vista cards (default)
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{stores.length} sucursal(es) · Datos de {new Date(data?.loaded_at).toLocaleTimeString('es-AR')}</p>
        <div className="relative">
          {showAllWarning && (
            <div className="absolute right-0 bottom-full mb-2 w-72 bg-bg-surface border border-border rounded-lg p-3 shadow-xl z-10">
              <p className="text-xs text-amber-300 mb-2">⚠ Esto mostrará todos los canvas simultáneamente. Con muchas sucursales puede ser pesado.</p>
              <div className="flex gap-2">
                <Button variant="ghost" className="text-xs" onClick={() => setShowAllWarning(false)}>Cancelar</Button>
                <Button variant="secondary" className="text-xs" onClick={() => {
                  setShowAllWarning(false);
                  // Para "ver todo" simplemente iteramos por todas las stores mostrando sus canvas apilados
                  // Usamos selectedStore=null pero mostramos todas en modo stacked
                  document.getElementById('mp-map-all')?.scrollIntoView({ behavior: 'smooth' });
                }}>Ver de todas formas</Button>
              </div>
            </div>
          )}
          <Button variant="secondary" className="text-xs" onClick={() => setShowAllWarning(!showAllWarning)}>
            Ver todo ⚠
          </Button>
        </div>
      </div>

      <StoreCards stores={stores} onSelect={setSelectedStore} />

      {orphans.length > 0 && <OrphanTerminals orphans={orphans} navigate={navigate} />}

      {/* Ver todo: canvas apilados (se muestra cuando showAllWarning fue aceptado) */}
      <div id="mp-map-all" />
    </div>
  );
}
